import React from 'react';
import { logger } from './lib/logger';
import { useT } from './i18n/index';

interface Props {
  children: React.ReactNode;
  /** Optional label shown in the error UI to identify which part of the app failed. */
  label?: string;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * Catches uncaught React render errors and shows a recovery UI instead of a
 * blank white screen. Wrap top-level route sections in App.tsx.
 *
 * Note: error boundaries only catch errors during rendering, in lifecycle
 * methods, and in constructors of child components. They do NOT catch errors
 * in event handlers (those need try/catch at the call-site).
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : String(error);
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    logger.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return <ErrorFallback message={this.state.message} label={this.props.label} />;
  }
}

function ErrorFallback({ message, label }: { message: string; label?: string }) {
  const { t } = useT();
  const handleReload = () => window.location.reload();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100dvh',
        background: '#f8fafc',
        fontFamily: 'system-ui, sans-serif',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
        {label ? t('somethingWentWrongIn', { label }) : t('somethingWentWrong')}
      </h1>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24, maxWidth: 400 }}>
        {t('unexpectedError')}
      </p>
      {process.env.NODE_ENV !== 'production' && (
        <pre
          style={{
            fontSize: 12,
            color: '#ef4444',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 24,
            maxWidth: 600,
            textAlign: 'left',
            overflowX: 'auto',
          }}
        >
          {message}
        </pre>
      )}
      <button
        onClick={handleReload}
        style={{
          background: '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          padding: '8px 20px',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {t('reloadPage')}
      </button>
    </div>
  );
}
